import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Papa from "papaparse";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { ChartBar, DownloadSimple, Printer } from "@phosphor-icons/react";

const RANGES = [
  { v: 7, l: "7 days" },
  { v: 30, l: "30 days" },
  { v: 90, l: "90 days" },
];
const BAR_COLORS = ["#2563eb", "#0ea5e9", "#6366f1", "#0891b2", "#7c3aed", "#0d9488", "#4f46e5", "#0284c7"];

function Kpi({ label, value, accent }) {
  return (
    <div className="p-5 border-r border-slate-200 last:border-r-0">
      <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">{label}</div>
      <div className={`font-mono text-2xl font-semibold mt-1 tabular-nums ${accent || ""}`}>{value}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, empty }) {
  return (
    <div className="border border-slate-200 bg-white rounded-md p-5">
      <div className="mb-4">
        <div className="font-heading text-lg font-semibold text-slate-900">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {empty ? (
        <div className="h-[220px] grid place-items-center text-sm text-slate-400">No data in this period.</div>
      ) : (
        <div className="h-[260px]">{children}</div>
      )}
    </div>
  );
}

export default function Reports() {
  const { hideMoney, maskMoney } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfrBreakdown, setMfrBreakdown] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setMfrBreakdown(data.manufacturer_breakdown || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/reports/summary", { params: { days } })
      .then(({ data }) => { if (active) setData(data); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days]);

  const canValue = data?.can_value;
  const cutsPerDay = (data?.cuts_per_day || []).map((d) => ({ ...d, day: d.date.slice(5) }));
  const hasCuts = cutsPerDay.some((d) => d.meters > 0);
  const topProducts = data?.top_products || [];
  const valueByMfr = data?.value_by_manufacturer || [];

  const exportCsv = () => {
    if (!data) return;
    const rows = (data.cuts_per_day || []).map((d) => ({
      section: "cuts_per_day", date: d.date, cuts: d.cuts, meters: d.meters,
      ...(canValue ? { consumed_value: d.value } : {}),
    }));
    const tops = (data.top_products || []).map((t) => ({
      section: "top_products", name: t.name, detail: t.label, meters: t.meters, cuts: t.cuts,
      ...(canValue ? { value: t.value } : {}),
    }));
    const csv = Papa.unparse([...rows, ...tops]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `vwa-report-${days}d.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="reports-page">
      <style>{`@media print { aside { display: none !important; } .no-print { display: none !important; } }`}</style>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Insights</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <ChartBar size={32} /> Analytics
          </h1>
          <p className="text-sm text-slate-600 mt-2">Consumption trends, top sellers, roll counts by manufacturer and stock value.</p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <div className="flex border border-slate-200 rounded-md overflow-hidden">
            {RANGES.map((r) => (
              <button key={r.v} onClick={() => setDays(r.v)} data-testid={`range-${r.v}`}
                className={`px-3 py-2 text-xs font-medium ${days === r.v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{r.l}</button>
            ))}
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={!data} className="gap-2 rounded-md" data-testid="reports-export-csv"><DownloadSimple size={16} /> CSV</Button>
          <Button variant="outline" onClick={() => window.print()} className="gap-2 rounded-md" data-testid="reports-print"><Printer size={16} /> PDF</Button>
        </div>
      </header>

      {loading || !data ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          <div className={`grid grid-cols-2 ${canValue ? "md:grid-cols-5" : "md:grid-cols-3"} border border-slate-200 bg-white mb-6 rounded-md overflow-hidden`}>
            <Kpi label="Stock on hand" value={`${data.kpis.stock_meters.toFixed(2)}m`} />
            <Kpi label={`Cuts (${days}d)`} value={data.kpis.cuts_period} />
            <Kpi label={`Meters cut (${days}d)`} value={`${data.kpis.meters_cut_period.toFixed(2)}m`} />
            {canValue && <Kpi label="Stock value" value={maskMoney(`$${data.kpis.stock_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)} accent="text-blue-600" />}
            {canValue && <Kpi label={`Consumed value (${days}d)`} value={maskMoney(`$${data.kpis.revenue_period.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)} accent="text-emerald-600" />}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Meters cut per day" subtitle={`Last ${days} days`} empty={!hasCuts}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cutsPerDay} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="meters" name="Meters" fill="#2563eb" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Top products by meters cut" subtitle="Most consumed colours" empty={topProducts.length === 0}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topProducts} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#334155" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="meters" name="Meters cut" radius={[0, 3, 3, 0]}>
                    {topProducts.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Rolls by manufacturer" subtitle="Active roll count per brand" empty={mfrBreakdown.length === 0}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mfrBreakdown} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="manufacturer" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="rolls" name="Rolls" radius={[3, 3, 0, 0]}>
                    {mfrBreakdown.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {canValue && !hideMoney && (
              <ChartCard title="Current stock value by manufacturer" subtitle="Sell price × meters on hand" empty={valueByMfr.length === 0}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={valueByMfr} margin={{ top: 8, right: 8, left: -4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="value" name="Stock value" radius={[3, 3, 0, 0]}>
                      {valueByMfr.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </>
      )}
    </div>
  );
}
